import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";
import { smoothScrollTo } from "../../lib/scroll";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const items = [
    { id: "home", label: t.nav.home },
    { id: "biochar", label: t.nav.biochar },
    { id: "production", label: t.nav.production },
    { id: "applications", label: t.nav.applications },
    { id: "documentation", label: t.nav.documentation },
    { id: "market", label: t.nav.market },
    { id: "contact", label: t.nav.contact },
    { id: "team", label: t.nav.team, route: "/team" },
  ];

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      setTimeout(() => smoothScrollTo(id), 60);
    } else {
      navigate("/#" + id);
    }
  };

  const goRoute = (route) => {
    setOpen(false);
    navigate(route);
    window.scrollTo(0, 0);
  };

  const handle = (item) => (item.route ? goRoute(item.route) : goSection(item.id));

  const goHome = () => {
    setOpen(false);
    if (location.pathname === "/") smoothScrollTo("home");
    else goRoute("/");
  };

  const solid = scrolled || open || location.pathname !== "/";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        solid ? "bg-white/85 backdrop-blur-xl border-border" : "bg-transparent border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-[72px] flex items-center justify-between">
        <button onClick={goHome} className="flex items-center" data-testid="brand-logo">
          <img src="/nerochar-logo.png" alt="NeroChar" className="h-5 lg:h-6 w-auto" />
        </button>

        <div className="flex items-center gap-3 sm:gap-5">
          <div className="flex items-center gap-1.5 font-body text-sm font-semibold text-zinc-400" data-testid="lang-switch">
            {["de", "en", "fr"].map((code, i) => (
              <span key={code} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-zinc-300">/</span>}
                <button
                  onClick={() => setLang(code)}
                  className={`transition-colors hover:text-nero-ink ${lang === code ? "text-nero-ink" : ""}`}
                  data-testid={`lang-${code}`}
                >
                  {code.toUpperCase()}
                </button>
              </span>
            ))}
          </div>

          {/* Single menu tab */}
          <div className="relative" ref={ref}>
            <button
              onClick={() => setOpen((o) => !o)}
              className="group inline-flex items-center gap-2.5 border border-nero-ink px-4 py-2.5 text-sm font-medium text-nero-ink hover:bg-nero-ink hover:text-white transition-colors"
              data-testid="menu-trigger"
              aria-expanded={open}
            >
              <span className="flex flex-col gap-[5px]">
                <span className={`block h-px w-5 bg-current transition-transform duration-300 ${open ? "translate-y-[6px] rotate-45" : ""}`} />
                <span className={`block h-px w-5 bg-current transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
                <span className={`block h-px w-5 bg-current transition-transform duration-300 ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
              </span>
              {t.nav.menu}
            </button>

            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="absolute right-0 mt-3 w-[min(88vw,360px)] bg-white border border-border shadow-xl"
                  data-testid="menu-panel"
                >
                  <nav className="py-2">
                    {items.map((l, i) => (
                      <button
                        key={l.id}
                        onClick={() => handle(l)}
                        className="group w-full flex items-center justify-between gap-4 px-6 py-4 text-left border-b border-border/60 last:border-b-0 hover:bg-background transition-colors"
                        data-testid={`menu-item-${l.id}`}
                      >
                        <span className="flex items-center gap-4">
                          <span className="font-heading text-xs font-bold tabular-nums text-nero-green">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="font-heading text-lg font-medium tracking-tight text-nero-ink">
                            {l.label}
                          </span>
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-zinc-300 group-hover:text-nero-green transition-colors" strokeWidth={1.75} />
                      </button>
                    ))}
                  </nav>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};
